import {useMemo} from 'react'

import type {DraftOrExistingIssue} from '../content-preview-types'
import {useContentPreview} from '../ContentPreviewContext'

export type TreeNode<T> = {
  item: T
  parent: TreeNode<T> | null
  children: Array<TreeNode<T>>
  blockedBy: Array<TreeNode<T>>
  isBlocking: Array<TreeNode<T>>
}

export function visit<T>(node: TreeNode<T>, callback: (node: TreeNode<T>) => void): void {
  callback(node)
  for (const child of node.children) {
    visit(child, callback)
  }
}

export function visitDependencies<T>(
  node: TreeNode<T>,
  callback: (node: TreeNode<T>) => void,
  visited: Set<TreeNode<T>> = new Set(),
): void {
  if (visited.has(node)) return
  visited.add(node)
  callback(node)

  for (const child of node.children) {
    visitDependencies(child, callback, visited)
  }
  for (const blockedBy of node.blockedBy) {
    visitDependencies(blockedBy, callback, visited)
  }
  for (const isBlocking of node.isBlocking) {
    visitDependencies(isBlocking, callback, visited)
  }
}

export async function visitDependenciesAsync<T>(
  node: TreeNode<T>,
  callback: (node: TreeNode<T>) => Promise<void>,
  visited: Set<TreeNode<T>> = new Set(),
): Promise<void> {
  if (visited.has(node)) return
  visited.add(node)
  await callback(node)

  for (const child of node.children) {
    await visitDependenciesAsync(child, callback, visited)
  }
  for (const blockedBy of node.blockedBy) {
    await visitDependenciesAsync(blockedBy, callback, visited)
  }
  for (const isBlocking of node.isBlocking) {
    await visitDependenciesAsync(isBlocking, callback, visited)
  }
}

export function toTreeMap<T, TKey = keyof T>(
  items: T[],
  keySelector: (item: T) => TKey,
  parentKeySelector: (item: T) => TKey | undefined,
  blockedByKeySelector?: (item: T) => TKey[] | undefined,
  isBlockingKeySelector?: (item: T) => TKey[] | undefined,
): Map<TKey, TreeNode<T>> {
  // Seed a Map of all items by key
  const itemMap = items.reduce((map, item) => {
    map.set(keySelector(item), {item, parent: null, children: [], blockedBy: [], isBlocking: []})
    return map
  }, new Map<TKey, TreeNode<T>>())

  for (const item of items) {
    const currentNode = itemMap.get(keySelector(item))
    const parentKey = parentKeySelector(item)

    // Link parents by parentKey
    if (parentKey != null) {
      const parentNode = itemMap.get(parentKey)
      if (parentNode && currentNode) {
        currentNode.parent = parentNode
        parentNode.children.push(currentNode)
      }
    }

    // Add dependencies
    const blockedByKeys = blockedByKeySelector?.(item) ?? []
    for (const blockedByKey of blockedByKeys) {
      const dependencyNode = itemMap.get(blockedByKey)
      // Prevent circular dependency. If A is blocked by B, B cannot also be blocked by A.
      if (dependencyNode && currentNode && !dependencyNode.blockedBy.includes(currentNode)) {
        if (!currentNode.blockedBy.includes(dependencyNode)) {
          currentNode.blockedBy.push(dependencyNode)
        }
        if (!dependencyNode.isBlocking.includes(currentNode)) {
          dependencyNode.isBlocking.push(currentNode)
        }
      }
    }

    const isBlockingKeys = isBlockingKeySelector?.(item) ?? []
    for (const isBlockingKey of isBlockingKeys) {
      const dependencyNode = itemMap.get(isBlockingKey)
      // Prevent circular dependency. If A is blocking B, B cannot also be blocking A.
      if (dependencyNode && currentNode && !dependencyNode.isBlocking.includes(currentNode)) {
        if (!currentNode.isBlocking.includes(dependencyNode)) {
          currentNode.isBlocking.push(dependencyNode)
        }
        if (!dependencyNode.blockedBy.includes(currentNode)) {
          dependencyNode.blockedBy.push(currentNode)
        }
      }
    }
  }

  // Return the complete Map; callers can decide what subtree they need
  return itemMap
}

/**
 * Convert content preview draft issue items into a tree structure.
 *
 * @returns a `Map` of all draft issues, where
 *   - `key` is the issue `tag`
 *   - `value` is a `TreeNode`, containing the draft issue, and pointers to its parent and children
 */
export function useDraftIssueTreeMap() {
  const {items, allOpenItems, versionedItems} = useContentPreview()

  return useMemo(() => {
    const openItemsSet = new Set(allOpenItems)
    const latestDraftIssues = [...versionedItems.entries()]
      .filter(([id]) => id.startsWith('new-issue:') || id.startsWith('existing-issue:'))
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(([_id, versionIds]) => {
        // For open tabs, we want to honour the selected version.
        const openedVersionId = versionIds.find(id => openItemsSet.has(id))
        if (openedVersionId) {
          return items.get(openedVersionId)
        }
        // For items that aren't open in preview, default to the latest version.
        const latestVersionId = versionIds.at(-1)
        return latestVersionId ? items.get(latestVersionId) : undefined
      })
      .filter((x): x is DraftOrExistingIssue => !!x)

    return toTreeMap(
      latestDraftIssues,
      item => item.tag,
      item => item.parentTag,
      item => item.blockedByTags,
      item => item.isBlockingTags,
    )
  }, [items, allOpenItems, versionedItems])
}
